import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Clock, Star, ChevronRight, RotateCcw, ChevronDown, Search, User, MessageCircle, Trophy, Volume2, VolumeX } from 'lucide-react';
import './CardGame.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Card suits and their colors
const SUITS = [
  { symbol: '\u2660', color: '#1a1a1a' },
  { symbol: '\u2665', color: '#dc2626' },
  { symbol: '\u2666', color: '#dc2626' },
  { symbol: '\u2663', color: '#1a1a1a' },
];
const CARD_VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

// Poker hand evaluation
const CARD_NUM = { 'A': 14, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13 };
const HAND_NAMES = ['Carta Alta', 'Par', 'Dos Pares', 'Tercia', 'Escalera', 'Color', 'Full House', 'Poker'];

function evaluateFiveCards(hand) {
  const vals = hand.map(c => CARD_NUM[c.value]).sort((a, b) => b - a);
  const suits = hand.map(c => c.suit);
  const isFlush = suits.every(s => s === suits[0]);
  const unique = [...new Set(vals)].sort((a, b) => b - a);
  const isStraight = unique.length === 5 && (unique[0] - unique[4] === 4);
  const isLowStraight = unique.length === 5 && unique[0] === 14 && unique[1] === 5 && unique[4] === 2;

  const counts = {};
  vals.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
  const groups = Object.values(counts).sort((a, b) => b - a);

  if (groups[0] === 4) return { rank: 7, name: 'Poker' };
  if (groups[0] === 3 && groups[1] === 2) return { rank: 6, name: 'Full House' };
  if (isFlush) return { rank: 5, name: 'Color' };
  if (isStraight || isLowStraight) return { rank: 4, name: 'Escalera' };
  if (groups[0] === 3) return { rank: 3, name: 'Tercia' };
  if (groups[0] === 2 && groups[1] === 2) return { rank: 2, name: 'Dos Pares' };
  if (groups[0] === 2) return { rank: 1, name: 'Par' };
  return { rank: 0, name: 'Carta Alta' };
}

function getCombinations(arr, k) {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const [first, ...rest] = arr;
  const withFirst = getCombinations(rest, k - 1).map(c => [first, ...c]);
  const withoutFirst = getCombinations(rest, k);
  return [...withFirst, ...withoutFirst];
}

function evaluatePokerHand(collectedCards) {
  if (collectedCards.length === 0) return { rank: -1, name: 'Sin cartas', bestCards: [] };
  if (collectedCards.length < 5) {
    // Evaluate partial hand
    const hand = evaluateFiveCards([...collectedCards, ...Array(5 - collectedCards.length).fill({ value: '2', suit: 99 })].slice(0, 5));
    // Re-evaluate with actual cards only
    const vals = collectedCards.map(c => CARD_NUM[c.value]).sort((a, b) => b - a);
    const counts = {};
    vals.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
    const groups = Object.values(counts).sort((a, b) => b - a);
    if (collectedCards.length >= 4 && groups[0] === 4) return { rank: 7, name: 'Poker', bestCards: collectedCards };
    if (collectedCards.length >= 3 && groups[0] === 3 && groups.length >= 2 && groups[1] === 2) return { rank: 6, name: 'Full House', bestCards: collectedCards };
    if (collectedCards.length >= 3 && groups[0] === 3) return { rank: 3, name: 'Tercia', bestCards: collectedCards };
    if (groups[0] === 2 && groups.length >= 2 && groups[1] === 2) return { rank: 2, name: 'Dos Pares', bestCards: collectedCards };
    if (groups[0] === 2) return { rank: 1, name: 'Par', bestCards: collectedCards };
    return { rank: 0, name: 'Carta Alta', bestCards: collectedCards };
  }
  // 5+ cards: find best 5-card combination
  const combos = getCombinations(collectedCards, 5);
  let best = { rank: -1, name: 'Carta Alta', bestCards: [] };
  for (const combo of combos) {
    const result = evaluateFiveCards(combo);
    if (result.rank > best.rank) {
      best = { ...result, bestCards: combo };
    }
  }
  return best;
}

function preGenerateCardAppearances(count) {
  const appearances = [];
  for (let i = 0; i < count; i++) {
    appearances.push({
      leftValue: CARD_VALUES[Math.floor(Math.random() * CARD_VALUES.length)],
      leftSuit: Math.floor(Math.random() * 4),
      rightValue: CARD_VALUES[Math.floor(Math.random() * CARD_VALUES.length)],
      rightSuit: Math.floor(Math.random() * 4),
    });
  }
  return appearances;
}

// Table backgrounds (light gray, harmonious with app)
const TABLE_BACKGROUNDS = [
  { bg: '#e8e8e8' },
  { bg: '#ebebeb' },
  { bg: '#e5e5e5' },
  { bg: '#eaeaea' },
  { bg: '#e7e7e7' },
];

// Card sound - local file for reliability
const CARD_SOUND_URL = '/card-flip.mp3';

// Pre-load audio for instant playback
let cardAudio = null;
let soundEnabled = true;
const initAudio = () => {
  if (!cardAudio) {
    cardAudio = new Audio(CARD_SOUND_URL);
    cardAudio.volume = 0.6;
    cardAudio.preload = 'auto';
  }
};
const playCardSoundGlobal = () => {
  if (!soundEnabled) return;
  try {
    initAudio();
    const clone = cardAudio.cloneNode();
    clone.volume = 0.6;
    clone.play().catch(() => {});
  } catch (e) {}
};

// ========== APP-STYLE HEADER (white, same as Dashboard/Cuestionarios) ==========
const AppHeader = ({ onBack, backLabel = 'Menu Principal', user, onLogout }) => {
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <button onClick={onBack} className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors" data-testid="card-game-nav-back">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          <span>{backLabel}</span>
        </button>
      </div>
    </header>
  );
};

// ========== SPECIALTY MENU (white bg, same style as Dashboard) ==========
const SpecialtyMenu = ({ onSelectSpecialty, onBack, specialties, onViewGoldenCards, goldenCardsCount, onViewChallenges, pendingChallengesCount }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const specialtyItems = [
    { id: 'ginecologia', name: 'Ginecologia y Obstetricia', dbName: 'Ginecología y Obstetricia' },
    { id: 'cirugia', name: 'Cirugia', dbName: 'Cirugía' },
    { id: 'pediatria', name: 'Pediatria', dbName: 'Pediatría' },
    { id: 'medicina-interna', name: 'Medicina Interna', dbName: 'Medicina Interna' },
    { id: 'otros', name: 'Otros', dbName: 'Otros' }
  ];

  const getDeckCount = (dbName) => {
    const found = specialties.find(s => s.specialty === dbName);
    return found ? found.deck_count : 0;
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-16">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-12 text-center" data-testid="card-game-title">
          Juego de Cartas
        </h1>
        <nav className="text-center">
          <ul className="space-y-6 sm:space-y-8">
            {specialtyItems.map((item) => {
              const count = getDeckCount(item.dbName);
              return (
                <li key={item.id}>
                  <button
                    onClick={() => onSelectSpecialty(item.dbName)}
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    data-testid={`card-game-specialty-${item.id}`}
                    className="group flex items-center justify-center space-x-4 text-xl sm:text-2xl lg:text-3xl font-medium text-gray-700 hover:text-black transition-colors duration-300"
                  >
                    <span className={`relative w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-gray-400 transition-all duration-300 flex-shrink-0 ${hoveredItem === item.id ? 'border-black' : ''}`}>
                      <span className={`absolute inset-0.5 rounded-full bg-black transition-all duration-300 ${hoveredItem === item.id ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} />
                    </span>
                    <span className="transition-all duration-300 group-hover:tracking-wide">{item.name}</span>
                    {count > 0 && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{count}</span>}
                  </button>
                </li>
              );
            })}
            {/* Retos section */}
            <li className="pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={onViewChallenges}
                data-testid="view-card-challenges-btn"
                className="group flex items-center justify-center space-x-4 text-xl sm:text-2xl lg:text-3xl font-medium text-gray-700 hover:text-black transition-colors duration-300"
              >
                <Trophy size={24} className="text-gray-600 flex-shrink-0" />
                <span className="transition-all duration-300 group-hover:tracking-wide">Retos</span>
                {pendingChallengesCount > 0 && (
                  <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-black bg-amber-400 text-black rounded-full">{pendingChallengesCount}</span>
                )}
              </button>
            </li>
            {/* Golden Cards link */}
            <li className="pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={onViewGoldenCards}
                data-testid="view-golden-cards-btn"
                className="group flex items-center justify-center space-x-4 text-xl sm:text-2xl lg:text-3xl font-medium text-amber-600 hover:text-amber-700 transition-colors duration-300"
              >
                <Star size={24} className="text-amber-500 fill-amber-500 flex-shrink-0" />
                <span className="transition-all duration-300 group-hover:tracking-wide">Mis Cartas Doradas</span>
                {goldenCardsCount > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{goldenCardsCount}</span>}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

// ========== SUBTOPIC MENU (white bg, same style) ==========
const SubtopicMenu = ({ specialty, subtopics, onSelectSubtopic }) => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-8">
        <h1 className="text-2xl sm:text-3xl font-black text-black mb-6 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>{specialty}</h1>
        <div className="space-y-3">
          {subtopics.map((sub, i) => (
            <button
              key={i}
              onClick={() => onSelectSubtopic(sub.name)}
              data-testid={`card-game-subtopic-${i}`}
              className="w-full flex items-center border border-gray-200 rounded-xl p-4 hover:border-gray-400 hover:shadow-sm transition-all bg-white text-left"
            >
              <span className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0 mr-3" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-black text-base leading-snug">{sub.name}</p>
                <p className="text-gray-400 text-sm mt-0.5">{sub.deck_count} {sub.deck_count === 1 ? 'mazo' : 'mazos'}</p>
              </div>
              <span className="text-gray-300 text-xl ml-2 flex-shrink-0">›</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ========== DECK SELECTOR (card list style like Cuestionarios) ==========
const DeckSelector = ({ decks, onSelectDeck, specialty, subtopic }) => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-8">
        <h1 className="text-2xl sm:text-3xl font-black text-black mb-6 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {specialty}{subtopic ? ` - ${subtopic}` : ''}
        </h1>
        <div className="space-y-3">
          {decks.map((deck) => (
            <button
              key={deck.id}
              onClick={() => onSelectDeck(deck)}
              data-testid={`card-game-deck-${deck.id}`}
              className="w-full flex items-center border border-gray-200 rounded-xl p-4 hover:border-gray-400 hover:shadow-sm transition-all bg-white text-left"
            >
              <span className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0 mr-3" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-black text-base leading-snug flex items-center gap-2">
                  {deck.name}
                  {deck.has_golden_card && <Star size={16} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
                </p>
                <p className="text-gray-400 text-sm mt-0.5">{deck.total_cards} cartas</p>
              </div>
              <span className="text-gray-300 text-xl ml-2 flex-shrink-0">›</span>
            </button>
          ))}
          {decks.length === 0 && <p className="text-gray-400 text-center py-8">No hay mazos disponibles aún</p>}
        </div>
      </div>
    </div>
  );
};

// ========== COLLECTION VIEW (Mi Colección) ==========
const CollectionView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    apiClient.get('/card-game/collection')
      .then(res => setData(res.data))
      .catch(() => toast.error('Error al cargar colección'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (name) => setCollapsed(prev => ({ ...prev, [name]: !prev[name] }));

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Mi colección</h1>
            <p className="text-gray-500 text-sm mt-1">Completa mazos sin errores para desbloquear cartas doradas.</p>
          </div>
          <div className="text-right flex-shrink-0 ml-4">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wide">Colección</p>
            <p className="text-2xl font-black text-black">{data.total_completed} <span className="text-gray-400 font-bold">/ {data.total_decks}</span></p>
            <div className="flex items-center gap-2 mt-1 justify-end">
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-black rounded-full transition-all" style={{ width: `${data.percentage}%` }} />
              </div>
              <span className="text-xs font-bold text-gray-600">{data.percentage}%</span>
            </div>
          </div>
        </div>

        {/* Sections */}
        {data.sections.map((section) => {
          const isCollapsed = collapsed[section.name];
          const pct = section.total > 0 ? Math.round(section.completed / section.total * 100) : 0;

          return (
            <div key={section.name} className="mb-6" data-testid={`collection-section-${section.name}`}>
              {/* Section header */}
              <button onClick={() => toggle(section.name)} className="w-full flex items-center justify-between py-2 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-extrabold text-black" style={{ fontFamily: 'Nunito, sans-serif' }}>{section.name}</h2>
                  <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{section.completed} / {section.total}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 font-semibold">{pct}% completado</span>
                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-black rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <ChevronDown size={18} className={`text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
                </div>
              </button>

              {/* Cards grid */}
              {!isCollapsed && (
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3 mt-3">
                  {section.decks.map((deck) => (
                    deck.completed ? (
                      /* Gold card */
                      <div key={deck.id} className="collection-card-gold" data-testid={`gold-card-${deck.id}`}>
                        <Star size={20} className="text-amber-800 mb-1" />
                        <p className="text-xs font-black text-amber-900 text-center leading-tight px-1">{deck.name}</p>
                      </div>
                    ) : (
                      /* Gray locked card */
                      <div key={deck.id} className="collection-card-locked" data-testid={`locked-card-${deck.id}`}>
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#aaa" strokeWidth="1.5">
                          <path d="M12 2C8 2 5 5 5 8c0 2 1 4 3 5v3c0 1 1 2 2 2h4c1 0 2-1 2-2v-3c2-1 3-3 3-5 0-3-3-6-7-6z" />
                          <path d="M10 14h4M12 10v4" />
                        </svg>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {data.sections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No hay mazos disponibles aún.</p>
            <p className="text-gray-400 text-sm mt-1">El administrador debe subir archivos de Excel para que aparezcan aquí.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== MODE SELECTOR (white bg, with Duels-style challenge form) ==========
const ChallengeModeSelector = ({ deck, onSolo, onChallenge, user }) => {
  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [challengeUsername, setChallengeUsername] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [challengerMessage, setChallengerMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  const fetchActiveUsers = async (searchQuery = '') => {
    setLoadingUsers(true);
    try {
      const endpoint = searchQuery.length >= 2
        ? `/users/search?q=${encodeURIComponent(searchQuery)}&limit=1000`
        : '/users/active?limit=1000';
      const response = await apiClient.get(endpoint);
      const users = response.data || [];
      setActiveUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const filterUsersHandler = (query) => {
    setChallengeUsername(query);
    setShowUserList(true);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 2) {
      if (activeUsers.length === 0) fetchActiveUsers('');
      else setFilteredUsers(activeUsers);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => fetchActiveUsers(query), 300);
  };

  const selectUser = (userItem) => {
    setSelectedUser(userItem);
    setChallengeUsername(userItem.full_name);
    setShowUserList(false);
  };

  const sendChallenge = async () => {
    if (!selectedUser) return toast.error('Selecciona un usuario');
    setLoading(true);
    try {
      const form = new FormData();
      form.append('deck_id', deck.id);
      form.append('player2_email', selectedUser.email);
      form.append('challenger_message', challengerMessage.trim());
      const res = await apiClient.post('/card-game/challenge', form);
      toast.success('Reto creado. Es tu turno de jugar.');
      onChallenge(res.data.challenge_id, {
        id: selectedUser.id,
        name: selectedUser.full_name,
        image: selectedUser.profile_image
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear reto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-4 pt-24 pb-8">
        <h2 className="text-2xl font-bold text-black text-center mb-2">{deck.name}</h2>
        <p className="text-gray-500 text-center mb-8">{deck.total_cards} cartas</p>

        <button
          onClick={onSolo}
          data-testid="play-solo-btn"
          className="w-full px-8 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors mb-2"
        >
          Jugar Solo
        </button>
        <p className="text-gray-400 text-sm text-center mb-6">Contesta todas las cartas del mazo. Si fallas 3 veces, se cancela el juego y vuelves a empezar.</p>

        <div className="text-center">
          <h3 className="text-2xl font-bold text-black mb-2">Retar a un Companero</h3>
          <p className="text-gray-400 text-sm mb-6">Contesta las 15 cartas (o hasta 3 errores). Las correctas forman tu mano de poker. Gana la mejor mano.</p>
          {!showChallengeForm ? (
            <button
              data-testid="create-card-challenge-button"
              className="px-8 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
              onClick={async () => {
                setShowChallengeForm(true);
                setShowUserList(true);
                await fetchActiveUsers('');
              }}
            >
              Retar Usuario
            </button>
          ) : (
            <div className="bg-gray-50 rounded-xl p-6 text-left">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Selecciona un usuario</h4>
              <div className="mb-4 relative">
                <div className="relative cursor-pointer" onClick={() => {
                  if (activeUsers.length === 0) fetchActiveUsers('');
                  setShowUserList(!showUserList);
                }}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    data-testid="card-challenge-username-input"
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors"
                    placeholder={loadingUsers ? "Cargando usuarios..." : "Haz clic para ver usuarios..."}
                    value={challengeUsername}
                    onChange={(e) => filterUsersHandler(e.target.value)}
                    onFocus={() => { setFilteredUsers(activeUsers); setShowUserList(true); }}
                    disabled={loadingUsers}
                  />
                  <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 transition-transform ${showUserList ? 'rotate-180' : ''}`} size={20} />
                </div>
                {showUserList && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto mt-1">
                    {loadingUsers ? (
                      <div className="p-4 text-gray-500 text-sm text-center">
                        <div className="animate-spin w-5 h-5 border-2 border-gray-300 border-t-black rounded-full mx-auto mb-2" />
                        Cargando usuarios...
                      </div>
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map((u) => (
                        <div key={u.id} data-testid={`card-user-option-${u.id}`} className={`p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center space-x-3 ${selectedUser?.id === u.id ? 'bg-gray-100' : ''}`} onClick={() => selectUser(u)}>
                          {u.profile_image ? (
                            <img src={u.profile_image} alt={u.full_name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"><User size={20} className="text-gray-500" /></div>
                          )}
                          <div className="text-left">
                            <div className="font-semibold text-sm">{u.full_name}</div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-gray-500 text-sm text-center">{challengeUsername ? 'No se encontraron usuarios' : 'No hay usuarios disponibles'}</div>
                    )}
                  </div>
                )}
              </div>
              {selectedUser && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">Usuario seleccionado:</p>
                  <div className="flex items-center space-x-3">
                    {selectedUser.profile_image ? (
                      <img src={selectedUser.profile_image} alt={selectedUser.full_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center"><User size={24} className="text-gray-500" /></div>
                    )}
                    <div><p className="font-semibold">{selectedUser.full_name}</p><p className="text-sm text-gray-500">{selectedUser.email}</p></div>
                  </div>
                </div>
              )}
              {selectedUser && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageCircle size={16} className="inline mr-1" />
                    Mensaje para {selectedUser.full_name} (opcional)
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors resize-none"
                    placeholder="Ej: Te voy a ganar!"
                    rows={2}
                    maxLength={200}
                    value={challengerMessage}
                    onChange={(e) => setChallengerMessage(e.target.value)}
                    data-testid="card-challenger-message"
                  />
                  <p className="text-xs text-gray-400 mt-1">{challengerMessage.length}/200 caracteres</p>
                </div>
              )}
              <div className="flex space-x-4">
                <button
                  data-testid="send-card-challenge-button"
                  className="flex-1 px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-400"
                  onClick={sendChallenge}
                  disabled={!selectedUser || loading}
                >
                  {loading ? 'Enviando...' : 'Enviar Reto'}
                </button>
                <button
                  className="flex-1 px-6 py-3 bg-white text-black font-semibold rounded-lg border border-black hover:bg-gray-100 transition-colors"
                  onClick={() => { setShowChallengeForm(false); setChallengeUsername(''); setSelectedUser(null); setChallengerMessage(''); }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== PLAYING CARD COMPONENT ==========
const PlayingCard = ({ option, value, suit, isCorrect, isWrong, onClick, side, animState }) => {
  const suitData = SUITS[suit % SUITS.length];
  let cardClass = 'card-game-card';
  if (animState === 'entering') cardClass += ' card-entering';
  if (animState === 'exiting') cardClass += ' card-exiting';
  if (isCorrect) cardClass += ' card-correct';
  if (isWrong) cardClass += ' card-wrong';
  if (side === 'left') cardClass += ' card-left';
  if (side === 'right') cardClass += ' card-right';

  return (
    <div className={cardClass} onClick={onClick} data-testid={`playing-card-${side}`} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="card-inner">
        <div className="card-front">
          <div className="card-corner top-left">
            <span className="card-value" style={{ color: suitData.color }}>{value}</span>
            <span className="card-suit-small" style={{ color: suitData.color }}>{suitData.symbol}</span>
          </div>
          <div className="card-center"><p className="card-option-text">{option}</p></div>
          <div className="card-corner bottom-right">
            <span className="card-value" style={{ color: suitData.color }}>{value}</span>
            <span className="card-suit-small" style={{ color: suitData.color }}>{suitData.symbol}</span>
          </div>
          <div className="card-suit-watermark" style={{ color: suitData.color, opacity: 0.06 }}>{suitData.symbol}</div>
          {isCorrect && <div className="card-result-overlay correct"><svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg></div>}
          {isWrong && <div className="card-result-overlay wrong"><svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg></div>}
        </div>
      </div>
    </div>
  );
};

// ========== GOLDEN CARD MODAL ==========
const GoldenCardModal = ({ deckName, onContinue }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="golden-card-modal">
    <div className="golden-card-container animate-golden-entrance">
      <div className="golden-card">
        <div className="golden-card-inner">
          <div className="golden-shine" />
          <Star size={48} className="text-amber-900 mb-3" style={{ filter: 'drop-shadow(0 0 8px rgba(245,158,11,0.5))' }} />
          <h2 className="text-amber-900 font-black text-xl text-center mb-1">CARTA DORADA</h2>
          <p className="text-amber-800 font-bold text-center text-sm">{deckName}</p>
          <p className="text-amber-700 text-xs mt-2 text-center">+10 puntos</p>
        </div>
      </div>
      <button onClick={onContinue} data-testid="golden-card-continue" className="mt-6 px-6 py-2 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors">Continuar</button>
    </div>
  </div>
);

// ========== CHALLENGE RESULT SCREEN ==========
const ChallengeResultScreen = ({ result, user, onBack }) => {
  const isPlayer1 = result.player1_id === user?.id;
  const myCards = isPlayer1 ? result.player1_cards_completed : result.player2_cards_completed;
  const myTime = isPlayer1 ? result.player1_time_seconds : result.player2_time_seconds;
  const myPoker = isPlayer1 ? result.player1_poker_hand_name : result.player2_poker_hand_name;
  const oppCards = isPlayer1 ? result.player2_cards_completed : result.player1_cards_completed;
  const oppTime = isPlayer1 ? result.player2_time_seconds : result.player1_time_seconds;
  const oppPoker = isPlayer1 ? result.player2_poker_hand_name : result.player1_poker_hand_name;
  const oppName = isPlayer1 ? result.player2_name : result.player1_name;
  const oppImage = isPlayer1 ? result.player2_profile_image : result.player1_profile_image;
  const resultType = result.result_for_user;

  const formatTime = (s) => {
    if (!s && s !== 0) return '--';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="text-center max-w-md w-full" data-testid="challenge-result-screen">
        {/* Result banner */}
        <div className={`mb-6 p-6 rounded-xl ${
          resultType === 'win' ? 'bg-green-50 border-2 border-green-400' :
          resultType === 'lose' ? 'bg-red-50 border-2 border-red-400' :
          'bg-gray-50 border-2 border-gray-300'
        }`}>
          <Trophy size={56} className={`mx-auto mb-3 ${
            resultType === 'win' ? 'text-green-500' :
            resultType === 'lose' ? 'text-red-400' :
            'text-gray-400'
          }`} />
          <h2 className={`text-3xl font-black mb-1 ${
            resultType === 'win' ? 'text-green-700' :
            resultType === 'lose' ? 'text-red-600' :
            'text-gray-700'
          }`}>
            {resultType === 'win' ? '¡GANASTE!' : resultType === 'lose' ? '¡PERDISTE!' : '¡EMPATE!'}
          </h2>
          <p className="text-gray-500 text-sm">{result.deck_name}</p>
          {resultType === 'win' && <p className="text-green-600 text-xs mt-1 font-bold">+10 puntos</p>}
          {resultType === 'tie' && <p className="text-gray-500 text-xs mt-1 font-bold">+5 puntos</p>}
        </div>

        {/* VS Comparison */}
        <div className="bg-gray-50 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              {user?.profile_image ? (
                <img src={user.profile_image} alt="Yo" className="w-14 h-14 rounded-full object-cover mx-auto mb-2 border-2 border-black/20" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-2"><User size={22} className="text-gray-500" /></div>
              )}
              <p className="font-bold text-black text-sm mb-1">Tu</p>
              {myPoker && <p className="text-xs font-black text-black mb-1">{myPoker}</p>}
              <p className="text-lg font-black text-black">{myCards}/15</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1 mt-1"><Clock size={12} />{formatTime(myTime)}</p>
            </div>

            <span className="text-gray-300 font-black text-2xl px-3">VS</span>

            <div className="text-center flex-1">
              {oppImage ? (
                <img src={oppImage} alt={oppName} className="w-14 h-14 rounded-full object-cover mx-auto mb-2 border-2 border-black/20" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-2"><User size={22} className="text-gray-500" /></div>
              )}
              <p className="font-bold text-black text-sm mb-1">{oppName?.split(' ')[0]}</p>
              {oppPoker && <p className="text-xs font-black text-black mb-1">{oppPoker}</p>}
              <p className="text-lg font-black text-black">{oppCards}/15</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1 mt-1"><Clock size={12} />{formatTime(oppTime)}</p>
            </div>
          </div>
        </div>

        <button onClick={onBack} data-testid="challenge-result-back-btn" className="px-8 py-3 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors">
          Volver a Retos
        </button>
      </div>
    </div>
  );
};

// ========== POKER HAND RESULT SCREEN (after challenge ends) ==========
const PokerHandScreen = ({ collectedCards, totalCards, timeSeconds, pokerResult, onSubmitDone, deckName }) => {
  const [showFan, setShowFan] = useState(false);
  const [showHand, setShowHand] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowFan(true), 300);
    setTimeout(() => setShowHand(true), 1800);
  }, []);

  const fanCards = collectedCards.slice(0, 15);
  const total = fanCards.length;
  const fanAngle = Math.min(10, 100 / Math.max(total, 1));

  // Determine which cards are part of the best hand
  const bestCardIndices = new Set();
  if (pokerResult?.bestCards) {
    pokerResult.bestCards.forEach(bc => {
      const idx = fanCards.findIndex((fc, i) => !bestCardIndices.has(i) && fc.value === bc.value && fc.suit === bc.suit);
      if (idx !== -1) bestCardIndices.add(idx);
    });
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4" data-testid="poker-hand-screen">
      <h2 className="text-xl font-black text-black mb-1">{deckName}</h2>
      <p className="text-gray-500 text-sm mb-4">{collectedCards.length}/{totalCards} correctas - {Math.floor(timeSeconds / 60)}:{String(Math.floor(timeSeconds % 60)).padStart(2, '0')}</p>

      {/* Fan of collected cards */}
      <div className="relative w-full flex justify-center items-end mb-8" style={{ height: '300px' }}>
        {fanCards.map((card, i) => {
          const rot = (i - (total - 1) / 2) * fanAngle;
          const suitData = SUITS[card.suit % SUITS.length];
          const isInHand = bestCardIndices.has(i);
          return (
            <div
              key={i}
              className="absolute transition-all duration-700 ease-out"
              style={{
                width: '90px',
                height: '130px',
                transform: showFan
                  ? `rotate(${rot}deg) translateY(${isInHand && showHand ? '-70px' : '-30px'})`
                  : 'rotate(0deg) translateY(80px) scale(0.4)',
                transformOrigin: 'bottom center',
                opacity: showFan ? 1 : 0,
                transitionDelay: `${i * 50}ms`,
                zIndex: isInHand && showHand ? 50 + i : i,
                filter: showHand && !isInHand && bestCardIndices.size > 0 ? 'brightness(0.7)' : 'none',
              }}
              data-testid={`poker-fan-card-${i}`}
            >
              <div className={`w-full h-full bg-white rounded-xl flex flex-col items-center justify-center relative overflow-hidden transition-all duration-500 ${
                isInHand && showHand
                  ? 'border-[3px] border-black shadow-xl ring-2 ring-amber-400'
                  : 'border-2 border-gray-300 shadow-md'
              }`}>
                <span className="absolute top-1.5 left-2 text-sm font-black" style={{ color: suitData.color }}>{card.value}</span>
                <span className="absolute top-5 left-2 text-xs" style={{ color: suitData.color }}>{suitData.symbol}</span>
                <span className="text-4xl" style={{ color: suitData.color, opacity: 0.12 }}>{suitData.symbol}</span>
                <span className="absolute bottom-1.5 right-2 text-sm font-black rotate-180" style={{ color: suitData.color }}>{card.value}</span>
                <span className="absolute bottom-5 right-2 text-xs rotate-180" style={{ color: suitData.color }}>{suitData.symbol}</span>
              </div>
            </div>
          );
        })}
        {total === 0 && (
          <div className="text-gray-400 text-lg font-bold">Sin cartas correctas</div>
        )}
      </div>

      {/* Poker hand name */}
      <div className={`text-center transition-all duration-500 ${showHand ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <p className="text-gray-500 text-sm mb-1">Tu mano:</p>
        <h3 className="text-3xl font-black text-black mb-6" data-testid="poker-hand-name">{pokerResult.name}</h3>
      </div>

      <button onClick={onSubmitDone} data-testid="poker-continue-btn" className="px-8 py-3 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors">
        Continuar
      </button>
    </div>
  );
};

// ========== GAME OVER SCREEN ==========
const GameOverScreen = ({ cardsCompleted, totalCards, timeSeconds, won, onRestart, onBack, deckName }) => (
  <div className="min-h-screen bg-white flex items-center justify-center px-4">
    <div className="text-center max-w-md" data-testid="game-over-screen">
      {won ? (
        <>
          <Trophy size={64} className="mx-auto text-amber-500 mb-4" />
          <h2 className="text-3xl font-black text-black mb-2">{totalCards}/{totalCards} CARTAS</h2>
          <p className="text-gray-700 text-lg mb-1">Completaste "{deckName}"</p>
        </>
      ) : (
        <>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center"><span className="text-red-500 text-3xl font-bold">X</span></div>
          <h2 className="text-3xl font-black text-black mb-2">Game Over</h2>
          <p className="text-gray-700 text-lg mb-1">Llegaste a {cardsCompleted}/{totalCards} cartas</p>
        </>
      )}
      <p className="text-gray-400 mb-6"><Clock size={16} className="inline mr-1" />Tiempo: {Math.floor(timeSeconds / 60)}:{String(Math.floor(timeSeconds % 60)).padStart(2, '0')}</p>
      <div className="flex gap-3 justify-center">
        <button onClick={onRestart} data-testid="game-restart-btn" className="flex items-center gap-2 px-5 py-2.5 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors"><RotateCcw size={18} /> Reintentar</button>
        <button onClick={onBack} data-testid="game-back-btn" className="px-5 py-2.5 bg-white text-black font-bold rounded-lg border border-black hover:bg-gray-100 transition-colors">Volver</button>
      </div>
    </div>
  </div>
);

// ========== MAIN GAMEPLAY ==========
const CardGamePlay = ({ deck, onBack, challengeId, opponentInfo, user }) => {
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [timeSeconds, setTimeSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(true);
  const [animState, setAnimState] = useState('idle');
  const [cardSuits, setCardSuits] = useState([0, 1]);
  const [cardValues, setCardValues] = useState(['A', 'K']);
  const [tableBackground, setTableBackground] = useState(null);
  const [showGolden, setShowGolden] = useState(false);
  const [challengeResult, setChallengeResult] = useState(null);
  const timerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  // Challenge/Poker mode state
  const [pregenCards, setPregenCards] = useState([]);
  const [collectedCards, setCollectedCards] = useState([]);
  const [showPokerResult, setShowPokerResult] = useState(false);
  const [pokerResult, setPokerResult] = useState(null);
  const [cardCollectAnim, setCardCollectAnim] = useState(false);
  const [challengeErrors, setChallengeErrors] = useState(0);
  const [soloErrors, setSoloErrors] = useState(0);

  const isChallenge = !!challengeId && !!opponentInfo;

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    soundEnabled = !newMuted;
  };

  useEffect(() => {
    if (deck?.cards) {
      setCards(deck.cards);
      if (challengeId) {
        setPregenCards(preGenerateCardAppearances(deck.cards.length));
      }
    }
    setTableBackground(TABLE_BACKGROUNDS[Math.floor(Math.random() * TABLE_BACKGROUNDS.length)]);
    if (!challengeId) randomizeCardAppearance();
    initAudio();
  }, [deck, challengeId]);

  useEffect(() => {
    if (isRunning && !gameOver && !showPokerResult) {
      timerRef.current = setInterval(() => setTimeSeconds(prev => prev + 1), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning, gameOver, showPokerResult]);

  const randomizeCardAppearance = () => {
    setCardSuits([Math.floor(Math.random() * 4), Math.floor(Math.random() * 4)]);
    setCardValues([CARD_VALUES[Math.floor(Math.random() * CARD_VALUES.length)], CARD_VALUES[Math.floor(Math.random() * CARD_VALUES.length)]]);
  };

  // Get current card appearances (pre-generated for challenges, random for solo)
  const getCurrentAppearance = () => {
    if (isChallenge && pregenCards[currentIndex]) {
      return {
        leftValue: pregenCards[currentIndex].leftValue,
        leftSuit: pregenCards[currentIndex].leftSuit,
        rightValue: pregenCards[currentIndex].rightValue,
        rightSuit: pregenCards[currentIndex].rightSuit,
      };
    }
    return { leftValue: cardValues[0], leftSuit: cardSuits[0], rightValue: cardValues[1], rightSuit: cardSuits[1] };
  };

  const handleCardSelect = useCallback((answer) => {
    if (showResult || animState !== 'idle') return;
    setSelectedAnswer(answer);
    setShowResult(true);
    playCardSoundGlobal();
  }, [showResult, animState]);

  // Advance to next card / end game (triggered by the "Siguiente" button)
  const handleNext = useCallback(() => {
    if (!showResult || animState !== 'idle') return;
    const currentCard = cards[currentIndex];
    const isCorrect = selectedAnswer === currentCard.correct_answer;

    if (isChallenge) {
      // CHALLENGE MODE: continue until 15 cards or 3 errors, collect correct cards
      let newErrors = challengeErrors;
      let newCollected = collectedCards;

      if (isCorrect) {
        const appearance = pregenCards[currentIndex] || null;
        const cardData = selectedAnswer === 'A'
          ? { value: appearance ? appearance.leftValue : cardValues[0], suit: appearance ? appearance.leftSuit : cardSuits[0] }
          : { value: appearance ? appearance.rightValue : cardValues[1], suit: appearance ? appearance.rightSuit : cardSuits[1] };
        newCollected = [...collectedCards, cardData];
        setCollectedCards(newCollected);
        setCardCollectAnim(true);
        setTimeout(() => setCardCollectAnim(false), 600);
      } else {
        newErrors = challengeErrors + 1;
        setChallengeErrors(newErrors);
      }

      // End game if: all 15 done OR 3 errors reached
      const isLastCard = currentIndex + 1 >= cards.length;
      const maxErrorsReached = newErrors >= 3;

      if (isLastCard || maxErrorsReached) {
        setIsRunning(false);
        const result = evaluatePokerHand(newCollected);
        setPokerResult(result);
        setShowPokerResult(true);
      } else {
        setAnimState('exiting');
        playCardSoundGlobal();
        setTimeout(() => {
          setCurrentIndex(prev => prev + 1);
          setSelectedAnswer(null);
          setShowResult(false);
          setAnimState('entering');
          playCardSoundGlobal();
          setTimeout(() => setAnimState('idle'), 500);
        }, 600);
      }
    } else {
      // SOLO MODE: answer the WHOLE deck; up to 3 errors cancels the game
      let newErrors = soloErrors;
      if (!isCorrect) {
        newErrors = soloErrors + 1;
        setSoloErrors(newErrors);
      }
      const isLastCard = currentIndex + 1 >= cards.length;
      const maxErrorsReached = newErrors >= 3;

      if (maxErrorsReached) {
        // 3rd error → cancel the game now
        setIsRunning(false);
        setGameOver(true);
        setWon(false);
        submitResult(currentIndex + 1, timeSeconds, false);
      } else if (isLastCard) {
        // Completed the whole deck with 2 errors or fewer → win
        setIsRunning(false);
        setGameOver(true);
        setWon(true);
        submitResult(cards.length, timeSeconds, true);
      } else {
        setAnimState('exiting');
        playCardSoundGlobal();
        setTimeout(() => {
          setCurrentIndex(prev => prev + 1);
          setSelectedAnswer(null);
          setShowResult(false);
          randomizeCardAppearance();
          setAnimState('entering');
          playCardSoundGlobal();
          setTimeout(() => setAnimState('idle'), 500);
        }, 600);
      }
    }
  }, [showResult, animState, cards, currentIndex, selectedAnswer, timeSeconds, isChallenge, pregenCards, cardValues, cardSuits, collectedCards, challengeErrors, soloErrors]);

  // Label for the "next" button depending on context
  const getNextLabel = () => {
    const currentCard = cards[currentIndex];
    const isCorrect = selectedAnswer === currentCard?.correct_answer;
    const isLastCard = currentIndex + 1 >= cards.length;
    if (isChallenge) {
      if (isLastCard || (challengeErrors + (isCorrect ? 0 : 1)) >= 3) return 'Ver mi mano';
      return 'Siguiente';
    }
    // Solo mode
    if ((soloErrors + (isCorrect ? 0 : 1)) >= 3) return 'Ver resultado';
    if (isLastCard) return 'Finalizar';
    return 'Siguiente';
  };

  const handlePokerSubmitDone = async () => {
    // Submit challenge result with poker data
    const finalCollected = collectedCards;
    const result = pokerResult || evaluatePokerHand(finalCollected);
    try {
      const form = new FormData();
      form.append('cards_completed', finalCollected.length);
      form.append('time_seconds', timeSeconds);
      form.append('poker_hand_rank', result.rank);
      form.append('poker_hand_name', result.name);
      const submitRes = await apiClient.post(`/card-game/challenge/${challengeId}/submit`, form);

      if (submitRes.data.status === 'completed' || submitRes.data.status === 'waiting_player2') {
        try {
          const resultRes = await apiClient.get(`/card-game/challenge/${challengeId}/result`);
          if (resultRes.data.status === 'completed') {
            setChallengeResult(resultRes.data);
            setShowPokerResult(false);
            setGameOver(true);
            return;
          }
        } catch (e) { console.error(e); }
      }
      // Player1 finished first — show waiting
      setShowPokerResult(false);
      setGameOver(true);
    } catch (err) {
      console.error('Error submitting challenge result:', err);
      toast.error('Error al enviar resultado');
    }
  };

  const submitResult = async (completed, time, didWin) => {
    // Solo mode only
    try {
      const form = new FormData();
      form.append('deck_id', deck.id);
      form.append('cards_completed', completed);
      form.append('time_seconds', time);
      form.append('won', didWin);
      const res = await apiClient.post('/card-game/complete', form);
      if (res.data.golden_card_earned) setShowGolden(true);
    } catch (err) {
      console.error('Error submitting result:', err);
    }
  };

  const handleRestart = async () => {
    try {
      const res = await apiClient.get(`/card-game/deck/${deck.id}${isChallenge ? '' : '?full=true'}`);
      setCards(res.data.cards);
    } catch (err) {
      setCards(deck.cards);
    }
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setGameOver(false);
    setWon(false);
    setSoloErrors(0);
    setChallengeErrors(0);
    setTimeSeconds(0);
    setIsRunning(true);
    setAnimState('idle');
    setTableBackground(TABLE_BACKGROUNDS[Math.floor(Math.random() * TABLE_BACKGROUNDS.length)]);
    randomizeCardAppearance();
  };

  if (showGolden) return <GoldenCardModal deckName={deck.name} onContinue={() => setShowGolden(false)} />;

  // Show poker hand screen (after all 15 cards in challenge mode)
  if (showPokerResult && pokerResult) {
    return <PokerHandScreen collectedCards={collectedCards} totalCards={cards.length} timeSeconds={timeSeconds} pokerResult={pokerResult} onSubmitDone={handlePokerSubmitDone} deckName={deck.name} />;
  }

  // Show challenge result screen if both players completed
  if (gameOver && challengeResult) {
    return <ChallengeResultScreen result={challengeResult} user={user} onBack={onBack} />;
  }

  if (gameOver) {
    // For challenges where player1 finishes first (player2 hasn't played yet), show waiting message
    if (challengeId && !challengeResult) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
          <div className="text-center max-w-md" data-testid="challenge-waiting-screen">
            <Trophy size={56} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-black text-black mb-2">¡Completaste tu turno!</h2>
            <p className="text-gray-700 text-lg mb-1">{collectedCards.length}/{cards.length} correctas</p>
            <p className="text-gray-500 text-sm mb-1">Mano: <strong>{pokerResult?.name || 'N/A'}</strong></p>
            <p className="text-gray-400 mb-1"><Clock size={16} className="inline mr-1" />Tiempo: {Math.floor(timeSeconds / 60)}:{String(Math.floor(timeSeconds % 60)).padStart(2, '0')}</p>
            <p className="text-gray-500 text-sm mt-4 mb-6">Esperando a que {opponentInfo?.name?.split(' ')[0] || 'tu oponente'} juegue...</p>
            <button onClick={onBack} data-testid="challenge-waiting-back-btn" className="px-8 py-3 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors">Volver</button>
          </div>
        </div>
      );
    }
    return <GameOverScreen cardsCompleted={won ? cards.length : currentIndex} totalCards={cards.length} timeSeconds={timeSeconds} won={won} onRestart={handleRestart} onBack={onBack} deckName={deck.name} />;
  }

  const currentCard = cards[currentIndex];
  if (!currentCard || !tableBackground) return null;
  const minutes = Math.floor(timeSeconds / 60);
  const seconds = timeSeconds % 60;
  const appearance = getCurrentAppearance();

  return (
    <div className="card-game-table" style={{ background: tableBackground.bg }} data-testid="card-game-table">

      {/* Top Bar */}
      <div className="card-game-topbar">
        <button onClick={onBack} className="text-black/60 hover:text-black transition-colors" data-testid="card-game-back"><ArrowLeft size={22} /></button>

        {isChallenge && (
          <div className="flex items-center gap-4" data-testid="card-game-vs-header">
            <div className="flex flex-col items-center">
              {user?.profile_image ? (
                <img src={user.profile_image} alt="Yo" className="w-12 h-12 rounded-full object-cover border-2 border-black/20" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-black/10 border-2 border-black/20 flex items-center justify-center"><User size={20} className="text-black/60" /></div>
              )}
              <span className="text-black text-[10px] mt-1 font-bold">Tu</span>
            </div>
            <span className="text-black font-black text-2xl">VS</span>
            <div className="flex flex-col items-center">
              {opponentInfo?.image ? (
                <img src={opponentInfo.image} alt={opponentInfo.name} className="w-12 h-12 rounded-full object-cover border-2 border-black/20" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-black/10 border-2 border-black/20 flex items-center justify-center"><User size={20} className="text-black/60" /></div>
              )}
              <span className="text-black text-[10px] mt-1 font-bold">{opponentInfo?.name?.split(' ')[0]}</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Collected cards counter + error counter in challenge mode */}
          {isChallenge && (
            <div className="flex items-center gap-1.5">
              <div className={`flex items-center gap-1 bg-black/10 rounded-full px-2.5 py-1 transition-transform ${cardCollectAnim ? 'scale-110' : 'scale-100'}`} data-testid="collected-cards-counter">
                <span className="text-black text-xs font-black">{collectedCards.length}</span>
                <span className="text-[10px]">{SUITS[0].symbol}</span>
              </div>
              <div className="flex items-center gap-0.5 bg-red-100 rounded-full px-2 py-1" data-testid="challenge-error-counter">
                <span className="text-red-600 text-xs font-black">{challengeErrors}</span>
                <span className="text-red-400 text-[10px]">/3</span>
              </div>
            </div>
          )}
          {/* Error counter in solo mode (3 errors cancels the game) */}
          {!isChallenge && (
            <div className="flex items-center gap-0.5 bg-red-100 rounded-full px-2 py-1" data-testid="solo-error-counter">
              <span className="text-red-600 text-xs font-black">{soloErrors}</span>
              <span className="text-red-400 text-[10px]">/3</span>
            </div>
          )}
          <button onClick={toggleMute} className="text-black/40 hover:text-black transition-colors p-1" data-testid="card-game-mute-btn">
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <div className="card-game-timer" data-testid="card-game-timer"><Clock size={14} /><span>{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</span></div>
        </div>
      </div>

      {/* Progress */}
      <div className="card-game-progress">
        <div className="card-game-progress-bar"><div className="card-game-progress-fill" style={{ width: `${(currentIndex / cards.length) * 100}%` }} /></div>
        <span className="card-game-progress-text">{currentIndex + 1}/{cards.length}</span>
      </div>

      {/* Deck/Topic Name */}
      <div className="px-4 sm:px-8 mt-1 mb-1" data-testid="card-game-deck-name">
        <p className="text-black font-black text-lg sm:text-xl text-left">{deck.name}</p>
      </div>

      {/* Question */}
      <div className="card-game-question" data-testid="card-game-question"><p>{currentCard.question}</p></div>

      {/* Cards */}
      <div className="card-game-cards-area">
        <PlayingCard option={currentCard.option_a} value={appearance.leftValue} suit={appearance.leftSuit}
          isCorrect={showResult && selectedAnswer === 'A' && currentCard.correct_answer === 'A'}
          isWrong={showResult && selectedAnswer === 'A' && currentCard.correct_answer !== 'A'}
          onClick={!showResult && animState === 'idle' ? () => handleCardSelect('A') : null} side="left" animState={animState} />
        <PlayingCard option={currentCard.option_b} value={appearance.rightValue} suit={appearance.rightSuit}
          isCorrect={showResult && selectedAnswer === 'B' && currentCard.correct_answer === 'B'}
          isWrong={showResult && selectedAnswer === 'B' && currentCard.correct_answer !== 'B'}
          onClick={!showResult && animState === 'idle' ? () => handleCardSelect('B') : null} side="right" animState={animState} />
      </div>

      {showResult && selectedAnswer && (
        <div className="card-game-feedback" data-testid="card-game-feedback">
          {selectedAnswer !== currentCard.correct_answer && (
            <p className="card-game-correct-hint" data-testid="correct-answer-hint">La correcta era: <strong>{currentCard.correct_answer === 'A' ? currentCard.option_a : currentCard.option_b}</strong></p>
          )}
          {currentCard.justification && (
            <p className="card-game-justification" data-testid="card-game-justification">{currentCard.justification}</p>
          )}
          <button
            onClick={handleNext}
            disabled={animState !== 'idle'}
            data-testid="card-game-next-btn"
            className="mt-3 inline-flex items-center gap-2 px-6 py-2.5 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40"
          >
            {getNextLabel()}
            <ArrowRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

// ========== CHALLENGES VIEW (Retos - Pending + History) ==========
const ChallengesView = ({ user, onPlayChallenge }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const [pendingRes, historyRes] = await Promise.allSettled([
        apiClient.get('/card-game/challenges/pending'),
        apiClient.get('/card-game/challenges/history')
      ]);
      if (pendingRes.status === 'fulfilled') setPendingChallenges(pendingRes.value.data || []);
      if (historyRes.status === 'fulfilled') setCompletedChallenges(historyRes.value.data || []);
    } catch (err) {
      console.error('Error fetching challenges:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChallenges(); }, []);

  const handleReject = async (challengeId) => {
    try {
      await apiClient.post(`/card-game/challenge/${challengeId}/reject`);
      toast.success('Reto rechazado');
      setRejectingId(null);
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al rechazar reto');
    }
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;

  const myPending = pendingChallenges.filter(c => c.role === 'challenged');
  const mySent = pendingChallenges.filter(c => c.role === 'challenger');

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-8">
        <h1 className="text-2xl sm:text-3xl font-black text-black mb-6 text-center" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="card-challenges-title">Retos de Cartas</h1>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="challenges-pending-tab"
            className={`flex-1 py-3 text-center font-bold text-sm transition-colors ${activeTab === 'pending' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-gray-600'}`}
          >
            Pendientes ({myPending.length + mySent.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="challenges-history-tab"
            className={`flex-1 py-3 text-center font-bold text-sm transition-colors ${activeTab === 'history' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-gray-600'}`}
          >
            Terminados ({completedChallenges.length})
          </button>
        </div>

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-3">
            {/* Received challenges */}
            {myPending.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Retos recibidos</p>
                {myPending.map((c) => (
                  <div key={c.id} className="border border-gray-200 rounded-xl p-4 mb-3" data-testid={`pending-challenge-${c.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      {c.player1_profile_image ? (
                        <img src={c.player1_profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"><User size={18} className="text-gray-500" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-black text-sm">{c.player1_name}</p>
                        <p className="text-gray-400 text-xs">{c.deck_name} - {c.specialty}</p>
                      </div>
                      {c.ready_to_play ? (
                        <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-1 rounded-full">Listo</span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-500 font-bold px-2 py-1 rounded-full">Esperando</span>
                      )}
                    </div>
                    {c.challenger_message && (
                      <p className="text-sm text-gray-600 italic mb-2 pl-13">"{c.challenger_message}"</p>
                    )}
                    <p className="text-xs text-gray-400 mb-3">{c.status_message} - {formatDate(c.created_at)}</p>
                    
                    {c.ready_to_play ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => onPlayChallenge(c)}
                          data-testid={`play-challenge-${c.id}`}
                          className="flex-1 py-2.5 bg-black text-white font-bold text-sm rounded-lg hover:bg-gray-800 transition-colors"
                        >
                          Jugar Reto
                        </button>
                        {rejectingId === c.id ? (
                          <div className="flex gap-1">
                            <button onClick={() => handleReject(c.id)} className="px-3 py-2.5 bg-red-500 text-white font-bold text-xs rounded-lg">Si</button>
                            <button onClick={() => setRejectingId(null)} className="px-3 py-2.5 bg-gray-200 text-black font-bold text-xs rounded-lg">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setRejectingId(c.id)} className="px-4 py-2.5 border border-gray-300 text-gray-500 font-bold text-sm rounded-lg hover:bg-gray-50">Rechazar</button>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <div className="flex-1 py-2.5 bg-gray-100 text-gray-400 font-bold text-sm rounded-lg text-center">Esperando al retador...</div>
                        {rejectingId === c.id ? (
                          <div className="flex gap-1">
                            <button onClick={() => handleReject(c.id)} className="px-3 py-2.5 bg-red-500 text-white font-bold text-xs rounded-lg">Si</button>
                            <button onClick={() => setRejectingId(null)} className="px-3 py-2.5 bg-gray-200 text-black font-bold text-xs rounded-lg">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setRejectingId(c.id)} className="px-4 py-2.5 border border-gray-300 text-gray-500 font-bold text-sm rounded-lg hover:bg-gray-50">Rechazar</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Sent challenges (as player1) */}
            {mySent.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Retos enviados</p>
                {mySent.map((c) => (
                  <div key={c.id} className="border border-gray-200 rounded-xl p-4 mb-3" data-testid={`sent-challenge-${c.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      {c.player2_profile_image ? (
                        <img src={c.player2_profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"><User size={18} className="text-gray-500" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-black text-sm">vs {c.player2_name}</p>
                        <p className="text-gray-400 text-xs">{c.deck_name} - {c.specialty}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">{c.status_message} - {formatDate(c.created_at)}</p>
                    {!c.player1_completed && (
                      <button
                        onClick={() => onPlayChallenge(c)}
                        data-testid={`play-sent-challenge-${c.id}`}
                        className="mt-3 w-full py-2.5 bg-black text-white font-bold text-sm rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Jugar (Tu turno)
                      </button>
                    )}
                    {c.player1_completed && (
                      <p className="mt-2 text-xs text-green-600 font-bold">Ya jugaste. Esperando al oponente.</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {myPending.length === 0 && mySent.length === 0 && (
              <div className="text-center py-12">
                <Trophy size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-400 text-lg font-bold">No hay retos pendientes</p>
                <p className="text-gray-400 text-sm mt-1">Reta a un compañero desde cualquier mazo</p>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3">
            {completedChallenges.length === 0 ? (
              <div className="text-center py-12">
                <Trophy size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-400 text-lg font-bold">Sin retos terminados</p>
                <p className="text-gray-400 text-sm mt-1">Los resultados de tus retos aparecerán aquí</p>
              </div>
            ) : (
              completedChallenges.map((c) => {
                const isPlayer1 = c.player1_id === user?.id;
                const myCards = isPlayer1 ? c.player1_cards_completed : c.player2_cards_completed;
                const myTime = isPlayer1 ? c.player1_time_seconds : c.player2_time_seconds;
                const oppCards = isPlayer1 ? c.player2_cards_completed : c.player1_cards_completed;
                const oppTime = isPlayer1 ? c.player2_time_seconds : c.player1_time_seconds;
                const oppName = isPlayer1 ? c.player2_name : c.player1_name;
                const oppImage = isPlayer1 ? c.player2_profile_image : c.player1_profile_image;

                return (
                  <div key={c.id} className="border border-gray-200 rounded-xl p-4" data-testid={`completed-challenge-${c.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {oppImage ? (
                          <img src={oppImage} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"><User size={18} className="text-gray-500" /></div>
                        )}
                        <div>
                          <p className="font-bold text-black text-sm">vs {oppName}</p>
                          <p className="text-gray-400 text-xs">{c.deck_name}</p>
                        </div>
                      </div>
                      <span className={`text-sm font-black px-3 py-1 rounded-full ${
                        c.result_class === 'winner' ? 'bg-green-100 text-green-700' :
                        c.result_class === 'loser' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {c.result_message}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                      <div className="text-center">
                        <p className="font-bold text-black text-lg">{myCards}/15</p>
                        <p>Tu - {formatTime(myTime)}</p>
                      </div>
                      <span className="text-gray-300 font-black text-xl">VS</span>
                      <div className="text-center">
                        <p className="font-bold text-black text-lg">{oppCards}/15</p>
                        <p>{oppName?.split(' ')[0]} - {formatTime(oppTime)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 text-right">{formatDate(c.completed_at || c.created_at)}</p>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ========== MAIN CARD GAME COMPONENT ==========
const CardGame = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('specialties');
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [fullDeck, setFullDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [challengeId, setChallengeId] = useState(null);
  const [opponentInfo, setOpponentInfo] = useState(null);
  const [goldenCards, setGoldenCards] = useState([]);
  const [pendingChallengesCount, setPendingChallengesCount] = useState(0);

  useEffect(() => {
    fetchSpecialties();
    fetchGoldenCards();
    fetchPendingChallengesCount();
  }, []);

  // Deep-link: /juego-cartas?deck=<id>&solo=1 → jump straight into solo play
  useEffect(() => {
    const deckId = searchParams.get('deck');
    if (!deckId) return;
    (async () => {
      try {
        const res = await apiClient.get(`/card-game/deck/${deckId}?full=true`);
        setFullDeck(res.data);
        setSelectedDeck(res.data);
        setChallengeId(null);
        setOpponentInfo(null);
        setView('play');
      } catch (err) {
        toast.error('Error al cargar el mazo');
      }
    })();
  }, [searchParams]);

  const fetchSpecialties = async () => {
    try {
      const res = await apiClient.get('/card-game/specialties');
      setSpecialties(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchGoldenCards = async () => {
    try {
      const res = await apiClient.get('/card-game/golden-cards');
      setGoldenCards(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPendingChallengesCount = async () => {
    try {
      const res = await apiClient.get('/card-game/challenges/pending/count');
      setPendingChallengesCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  };

  const handleSelectSpecialty = async (specialty) => {
    setSelectedSpecialty(specialty);
    const spec = specialties.find(s => s.specialty === specialty);
    if (spec && spec.subtopics && spec.subtopics.length > 0) {
      setView('subtopics');
    } else {
      await fetchDecks(specialty, '');
      setView('decks');
    }
  };

  const handleSelectSubtopic = async (subtopic) => {
    setSelectedSubtopic(subtopic);
    await fetchDecks(selectedSpecialty, subtopic);
    setView('decks');
  };

  const fetchDecks = async (specialty, subtopic) => {
    try {
      const params = { specialty };
      if (subtopic) params.subtopic = subtopic;
      const res = await apiClient.get('/card-game/decks', { params });
      setDecks(res.data);
    } catch (err) { toast.error('Error al cargar mazos'); }
  };

  const handleSelectDeck = (deck) => { setSelectedDeck(deck); setView('mode'); };

  const handlePlaySolo = async () => {
    try {
      const res = await apiClient.get(`/card-game/deck/${selectedDeck.id}?full=true`);
      setFullDeck(res.data);
      setChallengeId(null);
      setOpponentInfo(null);
      setView('play');
    } catch (err) { toast.error('Error al cargar el mazo'); }
  };

  const handleChallenge = async (cId, opponent) => {
    try {
      const res = await apiClient.get(`/card-game/deck/${selectedDeck.id}`);
      setFullDeck(res.data);
      setChallengeId(cId);
      setOpponentInfo(opponent);
      setView('play');
    } catch (err) { toast.error('Error al cargar el mazo'); }
  };

  const handlePlayChallenge = async (challenge) => {
    try {
      const res = await apiClient.get(`/card-game/deck/${challenge.deck_id}`);
      setFullDeck(res.data);
      setChallengeId(challenge.id);
      const isPlayer1 = challenge.player1_id === user?.id;
      setOpponentInfo({
        id: isPlayer1 ? challenge.player2_id : challenge.player1_id,
        name: isPlayer1 ? challenge.player2_name : challenge.player1_name,
        image: isPlayer1 ? challenge.player2_profile_image : challenge.player1_profile_image
      });
      setView('play');
    } catch (err) { toast.error('Error al cargar el mazo del reto'); }
  };

  const goBack = () => {
    if (view === 'play') { 
      // If coming from a challenge, go back to challenges view
      if (challengeId) { setChallengeId(null); setOpponentInfo(null); setView('challenges'); return; }
      setView('mode'); return; 
    }
    if (view === 'mode') { setView('decks'); return; }
    if (view === 'decks') {
      const spec = specialties.find(s => s.specialty === selectedSpecialty);
      if (spec?.subtopics?.length > 0 && selectedSubtopic) { setView('subtopics'); setSelectedSubtopic(null); }
      else { setView('specialties'); setSelectedSpecialty(null); }
      return;
    }
    if (view === 'subtopics') { setView('specialties'); setSelectedSpecialty(null); return; }
    if (view === 'golden') { setView('specialties'); return; }
    if (view === 'challenges') { setView('specialties'); fetchPendingChallengesCount(); return; }
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  }

  const getBackLabel = () => {
    if (view === 'subtopics') return 'Juego de Cartas';
    if (view === 'decks') return selectedSubtopic || selectedSpecialty || 'Volver';
    if (view === 'mode') return 'Mazos';
    if (view === 'golden') return 'Juego de Cartas';
    if (view === 'challenges') return 'Juego de Cartas';
    return 'Menu Principal';
  };

  return (
    <div className="min-h-screen bg-white">
      {view !== 'play' && <AppHeader onBack={goBack} backLabel={getBackLabel()} user={user} onLogout={onLogout} />}

      {view === 'specialties' && (
        <SpecialtyMenu
          specialties={specialties}
          onSelectSpecialty={handleSelectSpecialty}
          onBack={() => navigate('/')}
          onViewGoldenCards={() => setView('golden')}
          goldenCardsCount={goldenCards.length}
          onViewChallenges={() => setView('challenges')}
          pendingChallengesCount={pendingChallengesCount}
        />
      )}
      {view === 'subtopics' && (
        <SubtopicMenu specialty={selectedSpecialty} subtopics={specialties.find(s => s.specialty === selectedSpecialty)?.subtopics || []} onSelectSubtopic={handleSelectSubtopic} />
      )}
      {view === 'decks' && <DeckSelector decks={decks} onSelectDeck={handleSelectDeck} specialty={selectedSpecialty} subtopic={selectedSubtopic} />}
      {view === 'mode' && selectedDeck && (
        <ChallengeModeSelector deck={selectedDeck} onSolo={handlePlaySolo} onChallenge={handleChallenge} user={user} />
      )}
      {view === 'golden' && <CollectionView />}
      {view === 'challenges' && <ChallengesView user={user} onPlayChallenge={handlePlayChallenge} />}
      {view === 'play' && fullDeck && (
        <CardGamePlay deck={fullDeck} challengeId={challengeId} opponentInfo={opponentInfo} onBack={goBack} user={user} />
      )}
    </div>
  );
};

export default CardGame;
